import { Injectable, ApplicationRef } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SwUpdate, VersionInstallationFailedEvent, VersionReadyEvent } from '@angular/service-worker';
import { concat, interval } from 'rxjs';
import { filter, first } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class UpdateService {

  constructor(
    private swUpdate: SwUpdate,
    private appRef: ApplicationRef,
    private _snackBar: MatSnackBar,
    
  ) {
    console.log('Corriendo updateService',{swUpdateEnabled: this.swUpdate.isEnabled});
    if (this.swUpdate.isEnabled) {
      // 1. Iniciar la comprobación automática
      this.verificarActualizacionesAutomaticamente();
      
      // 2. Suscribirse al evento de "Nueva Versión Lista"
      this.swUpdate.versionUpdates
        .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
        .subscribe(() => {
          this.mostrarAvisoDeActualizacion();
        });

      // 3. La versión nueva se detectó pero no se pudo instalar (ej. un archivo del servidor no coincide con
      //    ngsw.json). Sin este aviso el usuario queda en la versión vieja sin enterarse.
      this.swUpdate.versionUpdates
        .pipe(filter((evt): evt is VersionInstallationFailedEvent => evt.type === 'VERSION_INSTALLATION_FAILED'))
        .subscribe(evt => {
          console.error('No se pudo instalar la versión nueva:', evt.error);
          this._snackBar.open('No se pudo instalar la versión nueva. Cerrá todas las pestañas de Playmotur y volvé a abrir.', 'OK', { duration: 0 });
        });

      // 4. El service worker quedó en un estado del que no puede salir solo: recargar trae todo del servidor.
      this.swUpdate.unrecoverable.subscribe(evt => {
        console.error('Service worker en estado irrecuperable:', evt.reason);
        this._snackBar.open('Hay que recargar la página para seguir usando el sistema.', 'RECARGAR', { duration: 0 })
          .onAction().subscribe(() => document.location.reload());
      });
    }
  }

  /**
   * Revisa el servidor cada X tiempo para ver si subiste cambios.
   * Angular espera a que la app esté "estable" para no interrumpir cargas iniciales.
   */
  private verificarActualizacionesAutomaticamente() {
    // Esperar a que la app sea estable la primera vez
    const appIsStable$ = this.appRef.isStable.pipe(first(isStable => isStable === true));
    // Cada 10 minutos (puedes cambiarlo a 5 o 10 minutos)
    const everyMinute$ = interval(10* 60 * 1000); 

    concat(appIsStable$, everyMinute$).subscribe(async () => {
      try {
        console.log('Buscando actualizaciones en segundo plano...');
        const hayNueva = await this.swUpdate.checkForUpdate();
        if (hayNueva) console.log('¡Nueva versión detectada!');
      } catch (err) {
        console.error('Error al buscar actualizaciones:', err);
      }
    });
  }

  // En UpdateService
  private mostrarAvisoDeActualizacion() {
    const snack = this._snackBar.open('Nueva versión disponible', 'ACTUALIZAR', {
      duration: 0, // Se queda fijo hasta que el usuario interactúe
      horizontalPosition: 'center',
      verticalPosition: 'bottom'
    });

    snack.onAction().subscribe(() => {
      this.actualizarAhora();
    });
  }

  // Búsqueda a pedido (botón del menú del admin). Si hay una versión nueva, el service worker la descarga
  // y emite VERSION_READY, que muestra el aviso de siempre ("Nueva versión disponible · ACTUALIZAR").
  // Si el service worker no contesta (quedó degradado tras un error) checkForUpdate no resuelve nunca:
  // se corta a los 20 segundos para que el botón no quede girando.
  public async buscarActualizacion(): Promise<'nueva' | 'al-dia' | 'no-disponible' | 'sin-control' | 'sin-respuesta' | 'error'> {
    if (!this.swUpdate.isEnabled) return 'no-disponible'; // ej. en desarrollo (ng serve) no hay service worker
    // Pestaña cargada salteando el service worker (Ctrl+F5, o recién instalado): checkForUpdate esperaría
    // para siempre a que la controle. Con un F5 normal queda controlada.
    if (!navigator.serviceWorker?.controller) return 'sin-control';
    try {
      const limite = new Promise<'sin-respuesta'>(resolve => setTimeout(() => resolve('sin-respuesta'), 20000));
      const busqueda = this.swUpdate.checkForUpdate().then(hay => hay ? 'nueva' as const : 'al-dia' as const);
      const resultado = await Promise.race([busqueda, limite]);
      // checkForUpdate resuelve true con la versión ya descargada: se muestra el aviso con ACTUALIZAR.
      // (VERSION_READY también lo muestra; abrirlo de nuevo acá evita que otro mensaje lo tape.)
      if (resultado === 'nueva') this.mostrarAvisoDeActualizacion();
      return resultado;
    } catch (err) {
      console.error('Error al buscar actualizaciones:', err);
      return 'error';
    }
  }

  public actualizarAhora() {
    this.swUpdate.activateUpdate().then(() => {
      document.location.reload();
    });
  }
}